import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
};

export type PortalClientLoginRequest = {
  Username: string;
  Password: string;
};
export type PortalClientLoginJWTRequest = {
  JWToken: string;
};

export type PortalClientLoginResponse = {
  Success: boolean;
  ErrorMessage: string;
  AccountID: string;
  JWT: string;
  AccountName: string;
  Key: string;
  Signature: string;
  FirebaseToken: string;
  Country: string;
  GCMEnabled: string;
  EFTCollectEnabled: string;
  EFTSendEnabled: string;
  ACHFundEnabled: string;
  ACHWithdrawEnabled: string;
  USDEFTCollectEnabled: string;
  USDEFTSendEnabled: string;
  RTRWithdrawEnabled: string;
  RTPWithdrawEnabled: string;
  FedwireWithdrawEnabled: string;
  VisaDirectEnabled: string;
  InteracMoneyRequestEnabled: string;
  InboundEtransferEnabled: string;
  InteracBulkPayoutEnabled: string;
  PortalViewOnly: string;
  MoneyRequestGenerateUrlEnabled: string;
  KYCLightEnabled: string;
  SubAccountPermissions: string;
  ClientAccountsEnabled: string;
  AutoBalanceTransferEnabled: string;
  TransactionLabelOverrideEnabled: string;
  IQ11StandardEnabled: string;
  IQ11BoostEnabled: string;
  IQ11PlusEnabled: string;
  IQ11AdvantageEnabled: string;
  TransactionServiceDefault: string;
  PayLinkEnabled: string;
  CustomFlinksCredentialsSet: boolean;
  CustomPlaidCredentialsSet: boolean;
  CustomInveriteCredentialsSet: boolean;
  EnablePaylinkDecline: string;
  CreditCardEnabled: string;
  GooglePayEnabled: boolean;
  UserManagementEnabled: string;
  AccountingSoftwareIntegrationEnabled: boolean;
  AccountingSystemIntegrationEnabled: string;
  AccountTransferEnabled: string;
  PushToCardEnabled: string;
  RealTimeMoneyRequestEnabled: string;
  CustomGlobalPaySharedSecret: boolean;
  AccountType: vp.ClientPortalAccountType;
  Domain: string;
  MFAEnabled: boolean;
  PasswordResetRequired: boolean;
  UserPermissions: vp.ApiClientPermissions;
  AdditionalAccounts: { [key: string]: string };
  BrandingEmailEnabled: string;
  BrandingELinxEnabled: string;
  BrandingPortalEnabled: string;
  BrandingOnboardingEnabled: string;
  TransactionApprovalWorkflowEnabled: string;
  EmailDomainEnabled: vp.PseudoBool;
  ChatSupportEnabled: boolean;
  ParentAccountID: string;
  ParentAccountName: string;
  SubAccountType: string;
  InterestAccountEnabled: vp.PseudoBool;
  InteracVerificationEnabled: boolean;
  BillPayEnabled: boolean;
  AccountAndBillingEnabled: boolean;
  FundMyAccountEnabled: boolean;
  WithdrawMyAccountEnabled: boolean;
  BankAccountTransferEnabled: boolean;
  OperationalAccountsEnabled: boolean;
  PartnerBillsAccount: boolean;
  PaymentsEnabled: boolean;
  CollectionsEnabled: boolean;
  ShowSubAccountOrgChart: boolean;
  PushToCardProcessor: vp.PushToCardProcessor | '';
  CreditCardProcessor: vp.CreditCardProcessor | '';
  ACHFundingProcessor: vp.ACHFundingProcessor | '';
  ACHWithdrawProcessor: vp.ACHWithdrawProcessor | '';
  InteracMoneyRequestProcessor: vp.InteracMoneyRequestProcessor | '';
  InteracBulkPayoutProcessor: vp.InteracBulkPayoutProcessor | '';
  FedwireProcessor: vp.FedwireProcessor | '';
  RTPProcessor: vp.RTPProcessor | '';
  FednowProcessor: vp.FednowProcessor | '';
  InteracBasicVerificationEnabled: vp.PseudoBool;
  InteracEnhancedVerificationEnabled: vp.PseudoBool;
  SupportTicketEnabled: boolean;
  DigitalWalletsEnabled: boolean;
  MicroDepositBankAccountVerificationEnabled: boolean;
  CardBasicVerificationEnabled: boolean;
  CardEnhancedVerificationEnabled: boolean;
  FileConversionEnabled: boolean;
  FileSubmissionEnabled: boolean;
};

type ReturnTuple = [
  PortalClientLoginResponse | undefined,
  (requestParams: PortalClientLoginRequest | PortalClientLoginJWTRequest) => void,
  () => void,
  (requestParams: PortalClientLoginRequest | PortalClientLoginJWTRequest) => Promise<PortalClientLoginResponse>,
];

function normalizeResponse(res: vp.ClientAuthorizationResponse): PortalClientLoginResponse {
  return {
    ...res,
    AccountingSoftwareIntegrationEnabled: vp.toBool(res.AccountingSoftwareIntegrationEnabled),
    CustomFlinksCredentialsSet: res.CustomFlinksCredentialsSet || false,
    CustomPlaidCredentialsSet: res.CustomPlaidCredentialsSet || false,
    CustomInveriteCredentialsSet: res.CustomInveriteCredentialsSet || false,
    CustomGlobalPaySharedSecret: res.CustomGlobalPaySharedSecret || false,
    MFAEnabled: res.MFAEnabled || false,
    PasswordResetRequired: res.PasswordResetRequired || false,
    ChatSupportEnabled: res.ChatSupportEnabled || false,
    InteracVerificationEnabled: res.InteracVerificationEnabled || false,
    SupportTicketEnabled: res.SupportTicketEnabled || false,
    AdditionalAccounts: res.AdditionalAccounts || {},
    UserPermissions: res.UserPermissions || {},
    AccountID: res.AccountID || '',
    AccountName: res.AccountName || '',
    Key: res.Key || '',
    Signature: res.Signature || '',
    JWT: res.JWT || '',
    FirebaseToken: res.FirebaseToken || '',
    Country: res.Country || '',
    GCMEnabled: res.GCMEnabled || '',
    EFTCollectEnabled: res.EFTCollectEnabled || '',
    EFTSendEnabled: res.EFTSendEnabled || '',
    ACHFundEnabled: res.ACHFundEnabled || '',
    USDEFTCollectEnabled: res.USDEFTCollectEnabled || '',
    USDEFTSendEnabled: res.USDEFTSendEnabled || '',
    RTRWithdrawEnabled: res.RTRWithdrawEnabled || '',
    RTPWithdrawEnabled: res.RTPWithdrawEnabled || '',
    FedwireWithdrawEnabled: res.FedwireWithdrawEnabled || '',
    VisaDirectEnabled: res.VisaDirectEnabled || '',
    InteracMoneyRequestEnabled: res.InteracMoneyRequestEnabled || '',
    InboundEtransferEnabled: res.InboundEtransferEnabled || '',
    InteracBulkPayoutEnabled: res.InteracBulkPayoutEnabled || '',
    PortalViewOnly: res.PortalViewOnly || '',
    MoneyRequestGenerateUrlEnabled: res.MoneyRequestGenerateUrlEnabled || '',
    KYCLightEnabled: res.KYCLightEnabled || '',
    SubAccountPermissions: res.SubAccountPermissions || '',
    ClientAccountsEnabled: res.ClientAccountsEnabled || '',
    AutoBalanceTransferEnabled: res.AutoBalanceTransferEnabled || '',
    TransactionLabelOverrideEnabled: res.TransactionLabelOverrideEnabled || '',
    IQ11StandardEnabled: res.IQ11StandardEnabled || '',
    IQ11BoostEnabled: res.IQ11BoostEnabled || '',
    IQ11PlusEnabled: res.IQ11PlusEnabled || '',
    IQ11AdvantageEnabled: res.IQ11AdvantageEnabled || '',
    TransactionServiceDefault: res.TransactionServiceDefault || '',
    PayLinkEnabled: res.PayLinkEnabled || '',
    EnablePaylinkDecline: res.EnablePaylinkDecline || '',
    CreditCardEnabled: res.CreditCardEnabled || '',
    GooglePayEnabled: res.GooglePayEnabled || false,
    UserManagementEnabled: res.UserManagementEnabled || '',
    AccountingSystemIntegrationEnabled: res.AccountingSystemIntegrationEnabled || '',
    AccountTransferEnabled: res.AccountTransferEnabled || '',
    PushToCardEnabled: res.PushToCardEnabled || '',
    RealTimeMoneyRequestEnabled: res.RealTimeMoneyRequestEnabled || '',
    AccountType: res.AccountType || '',
    Domain: res.Domain || '',
    BrandingEmailEnabled: res.BrandingEmailEnabled || '',
    BrandingELinxEnabled: res.BrandingELinxEnabled || '',
    BrandingPortalEnabled: res.BrandingPortalEnabled || '',
    BrandingOnboardingEnabled: res.BrandingOnboardingEnabled || '',
    TransactionApprovalWorkflowEnabled: res.TransactionApprovalWorkflowEnabled || '',
    EmailDomainEnabled: res.EmailDomainEnabled || '0',
    ParentAccountID: res.ParentAccountID || '',
    ParentAccountName: res.ParentAccountName || '',
    SubAccountType: res.SubAccountType || '',
    InterestAccountEnabled: res.InterestAccountEnabled || '0',
    InteracBasicVerificationEnabled: res.InteracBasicVerificationEnabled || '0',
    InteracEnhancedVerificationEnabled: res.InteracEnhancedVerificationEnabled || '0',
    DigitalWalletsEnabled: res.DigitalWalletsEnabled || false,
    MicroDepositBankAccountVerificationEnabled: res.MicroDepositBankAccountVerificationEnabled || false,
    CardBasicVerificationEnabled: res.CardBasicVerificationEnabled || false,
    CardEnhancedVerificationEnabled: res.CardEnhancedVerificationEnabled || false,
    FileConversionEnabled: res.FileConversionEnabled || false,
    FileSubmissionEnabled: res.FileSubmissionEnabled || false,
    BillPayEnabled: res.BillPayEnabled || false,
    AccountAndBillingEnabled: res.AccountAndBillingEnabled || false,
    FundMyAccountEnabled: res.FundMyAccountEnabled || false,
    WithdrawMyAccountEnabled: res.WithdrawMyAccountEnabled || false,
    BankAccountTransferEnabled: res.BankAccountTransferEnabled || false,
    OperationalAccountsEnabled: res.OperationalAccountsEnabled || false,
    PartnerBillsAccount: res.PartnerBillsAccount || false,
    PaymentsEnabled: res.PaymentsEnabled || false,
    CollectionsEnabled: res.CollectionsEnabled || false,
    ShowSubAccountOrgChart: res.ShowSubAccountOrgChart || false,
    PushToCardProcessor: res.PushToCardProcessor || '',
    CreditCardProcessor: res.CreditCardProcessor || '',
    ACHFundingProcessor: res.ACHFundingProcessor || '',
    ACHWithdrawProcessor: res.ACHWithdrawProcessor || '',
    InteracMoneyRequestProcessor: res.InteracMoneyRequestProcessor || '',
    InteracBulkPayoutProcessor: res.InteracBulkPayoutProcessor || '',
    FedwireProcessor: res.FedwireProcessor || '',
    RTPProcessor: res.RTPProcessor || '',
    FednowProcessor: res.FednowProcessor || '',
  };
}

function usePostPortalClientLogin({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PortalClientLoginRequest | PortalClientLoginJWTRequest>();
  const [response, setResponse] = useState<PortalClientLoginResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);

  useEffect(() => {
    const sendRequest = async (requestParams: PortalClientLoginRequest | PortalClientLoginJWTRequest) => {
      const raw = await api.postPortalClientLogin(requestParams, source);
      const res = normalizeResponse(raw);

      setResponse(res);
      setSendRequest(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: PortalClientLoginRequest | PortalClientLoginJWTRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    async (requestParams: PortalClientLoginRequest | PortalClientLoginJWTRequest) => {
      const raw = await api.postPortalClientLogin(requestParams, source);
      const res = normalizeResponse(raw);
      setResponse(res);
      return res;
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostPortalClientLogin);
